/**
 * Manually derived from: https://shopify.dev/docs/api/admin-graphql/2023-04/objects/CompanyContact
 * API Version: 2022-10
 */

import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyCompanyContactKey,
  ShopifyCompanyContactRoleAssignmentKey,
  ShopifyCompanyContactRoleAssignmentToCompanyContactKey,
  ShopifyCompanyContactToCompanyKey,
  ShopifyCompanyContactToCustomerKey,
  ShopifyCompanyContactToDraftOrdersKey,
  ShopifyCompanyContactToMainContactForCompanyKey,
  ShopifyCompanyContactToOrdersKey,
  ShopifyCompanyContactToRoleAssignmentsKey,
  ShopifyCompanyContactToShopKey,
  ShopifyCompanyKey,
  ShopifyCompanyToCompanyContactKey,
  ShopifyCustomerKey,
  ShopifyDraftOrderKey,
  ShopifyDraftOrderToCompanyContactKey,
  ShopifyOrderKey,
  ShopifyOrderToCompanyContactKey,
  ShopifyShopKey,
} from "./keys";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyCompanyContactKey,
  "2022-10": {
    name: "Shopify Company Contact",
    graphQLTypename: "CompanyContact",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id" } },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "createdAt", shopifyFieldKey: "created_at" } },
      },
      {
        name: "Shopify Updated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "updatedAt", shopifyFieldKey: "updated_at" } },
      },
      {
        name: "Title",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "title", shopifyFieldKey: "title" } },
      },
      {
        name: "Company",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCompanyContactToCompanyKey,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "company", graphQLSelection: "id" } },
        configuration: {
          relatedModelKey: ShopifyCompanyKey,
        },
      },
      {
        name: "Main Contact for Company",
        fieldType: FieldType.HasOne,
        key: ShopifyCompanyContactToMainContactForCompanyKey,
        configuration: {
          relatedModelKey: ShopifyCompanyKey,
          inverseFieldKey: ShopifyCompanyToCompanyContactKey,
        },
      },
      {
        name: "Customer",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCompanyContactToCustomerKey,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "customer", graphQLSelection: "id" } },
        configuration: {
          relatedModelKey: ShopifyCustomerKey,
        },
      },
      {
        name: "Locale",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "locale" } },
      },
      {
        name: "Is Main Contact",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "isMainContact" } },
      },
      {
        name: "Lifetime Duration",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "lifetimeDuration" } },
      },
      {
        name: "Draft Orders",
        fieldType: FieldType.HasMany,
        key: ShopifyCompanyContactToDraftOrdersKey,
        configuration: {
          relatedModelKey: ShopifyDraftOrderKey,
          inverseFieldKey: ShopifyDraftOrderToCompanyContactKey,
        },
      },
      {
        name: "Orders",
        fieldType: FieldType.HasMany,
        key: ShopifyCompanyContactToOrdersKey,
        configuration: {
          relatedModelKey: ShopifyOrderKey,
          inverseFieldKey: ShopifyOrderToCompanyContactKey,
        },
      },
      {
        name: "Role Assignments",
        fieldType: FieldType.HasMany,
        key: ShopifyCompanyContactToRoleAssignmentsKey,
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifyCompanyContactRoleAssignmentKey,
          inverseFieldKey: ShopifyCompanyContactRoleAssignmentToCompanyContactKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCompanyContactToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      parent: "companies",
      resource: "company.contacts",
    },
  },

  "2025-04": {
    fields: [
      {
        name: "Locale",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "locale",
            shopifyFieldKey: "locale",
            availableInWebhook: ["company_contacts/create", "company_contacts/update"],
          },
        },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "createdAt",
            shopifyFieldKey: "created_at",
            availableInWebhook: ["company_contacts/create", "company_contacts/update"],
          },
        },
      },
      {
        name: "Shopify Updated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "updatedAt",
            shopifyFieldKey: "updated_at",
            availableInWebhook: ["company_contacts/create", "company_contacts/update"],
          },
        },
      },
      {
        name: "Title",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "title",
            shopifyFieldKey: "title",
            availableInWebhook: ["company_contacts/create", "company_contacts/update"],
          },
        },
      },
      {
        name: "Company",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCompanyContactToCompanyKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "company",
            graphQLSelection: "id",
            shopifyFieldKey: "company",
            availableInWebhook: ["company_contacts/create", "company_contacts/update"],
          },
        },
        configuration: {
          relatedModelKey: ShopifyCompanyKey,
        },
      },
    ],
  },
};

export const ShopifyCompanyContact = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
